import { DownloadIcon, FilterIcon, TrendingUpIcon } from "lucide-react";
import Container from "../global/container";
import { Button } from "../ui/button";
import { MagicCard } from "../ui/magic-card";

const Analysis = () => {
  return (
    <div
      className="relative flex flex-col items-center justify-center w-full py-20"
      data-oid="r::51oo"
    >
      <Container data-oid="32.5r:o">
        <div
          className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16"
          data-oid="poonhj_"
        >
          <h2
            className="text-2xl md:text-4xl lg:text-5xl font-heading font-medium !leading-snug"
            data-oid="5onw6ic"
          >
            Intelligent marketing <br data-oid="5zdbe8o" />
            <span className="font-subheading italic" data-oid="3aqsae_">
              dashboard
            </span>
          </h2>
          <p
            className="text-base md:text-lg text-accent-foreground/80 mt-4"
            data-oid="sgj3--g"
          >
            Gain detailed insights into your marketing performance and campaign
            metrics with our advanced analytics tools.
          </p>
        </div>
      </Container>

      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-6 relative w-full"
        data-oid="kfv7d8q"
      >
        <Container delay={0.2} data-oid="ztca.:s">
          <div
            className="rounded-2xl bg-background/40 relative border border-border/50"
            data-oid="1rh5::q"
          >
            <MagicCard
              gradientFrom="#38bdf8"
              gradientTo="#3b82f6"
              gradientColor="rgba(59,130,246,0.1)"
              className="p-4 lg:p-8 w-full overflow-hidden"
              data-oid="6mqw4fa"
            >
              <div
                className="absolute bottom-0 right-0 bg-blue-500 w-1/4 h-1/4 blur-[8rem] z-20"
                data-oid="f-cpqqt"
              ></div>
              <div className="space-y-4" data-oid="suyr6m5">
                <h3 className="text-xl font-semibold" data-oid="h5egxk:">
                  Campaign Insights
                </h3>
                <p className="text-sm text-muted-foreground" data-oid="tza4g1d">
                  Track your campaign performance with data-driven insights.
                </p>

                <div className="space-y-4" data-oid="fx2k4.q">
                  <div
                    className="flex justify-between items-baseline"
                    data-oid="ivzrgd9"
                  >
                    <div data-oid="s-67v22">
                      <div
                        className="text-3xl font-semibold"
                        data-oid="kd1oer0"
                      >
                        $12,834
                      </div>
                      <div
                        className="text-sm text-green-500 flex items-center gap-1 mt-2"
                        data-oid="6_:ctma"
                      >
                        <TrendingUpIcon
                          className="w-4 h-4"
                          data-oid="ikq.uav"
                        />
                        +25% from last month
                      </div>
                    </div>
                    <div className="flex gap-2" data-oid="uhb:vq1">
                      <Button size="icon" variant="ghost" data-oid="3rta57f">
                        <FilterIcon className="w-5 h-5" data-oid="oj1kvcy" />
                      </Button>
                      <Button size="icon" variant="ghost" data-oid="l6rkqlb">
                        <DownloadIcon className="w-5 h-5" data-oid="vr5q_74" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2" data-oid="d.rqa3m">
                    <div
                      className="grid grid-cols-4 text-sm text-muted-foreground py-2"
                      data-oid="ty5q:tl"
                    >
                      <div data-oid="rxf8qb0">Campaign</div>
                      <div data-oid="fj3lzqx">Status</div>
                      <div data-oid="f50l:n_">Reach</div>
                      <div data-oid="bp:uwy4">ROI</div>
                    </div>
                    {[
                      {
                        name: "Sales",
                        status: "Active",
                        reach: "45K",
                        roi: "+32%",
                      },
                      {
                        name: "Emails",
                        status: "Done",
                        reach: "28K",
                        roi: "+18%",
                      },
                      {
                        name: "Ads",
                        status: "Active",
                        reach: "62K",
                        roi: "+45%",
                      },
                    ].map((campaign) => (
                      <div
                        key={campaign.name}
                        className="grid grid-cols-4 text-sm py-2 border-t border-border/50"
                        data-oid="m-bq54-"
                      >
                        <div data-oid="hbttr17">{campaign.name}</div>
                        <div data-oid="h70q8bg">{campaign.status}</div>
                        <div data-oid="ln1gdbp">{campaign.reach}</div>
                        <div className="font-semibold" data-oid="_:d3x.-">
                          {campaign.roi}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </MagicCard>
          </div>
        </Container>

        <Container delay={0.2} data-oid="bp0srfb">
          <div
            className="rounded-2xl bg-background/40 relative border border-border/50"
            data-oid="k3povnd"
          >
            <MagicCard
              gradientFrom="#38bdf8"
              gradientTo="#3b82f6"
              gradientColor="rgba(59,130,246,0.1)"
              className="p-4 lg:p-8 w-full overflow-hidden"
              data-oid="d0xg8h5"
            >
              <div
                className="absolute bottom-0 right-0 bg-sky-500 w-1/4 h-1/4 blur-[8rem] z-20"
                data-oid="lqncxbq"
              ></div>
              <div className="space-y-4" data-oid="uxph5_8">
                <h3 className="text-xl font-semibold" data-oid="h6y.rrs">
                  Audience Metrics
                </h3>
                <p className="text-sm text-muted-foreground" data-oid="u3r0fre">
                  Understand your audience behavior and engagement patterns.
                </p>

                <div className="space-y-4" data-oid="8r0jdl6">
                  <div
                    className="flex justify-between items-baseline"
                    data-oid="i:a2ypl"
                  >
                    <div data-oid="t.auy96">
                      <div
                        className="text-3xl font-semibold"
                        data-oid="xrztsj."
                      >
                        84,392
                      </div>
                      <div
                        className="text-sm text-green-500 flex items-center gap-1 mt-2"
                        data-oid="zkyeg_s"
                      >
                        <TrendingUpIcon
                          className="w-4 h-4"
                          data-oid="gj2dcl0"
                        />
                        +12% engagement rate
                      </div>
                    </div>
                    <div className="flex gap-2" data-oid="i:6s:3.">
                      <Button size="icon" variant="ghost" data-oid="z.r7-_l">
                        <FilterIcon className="w-5 h-5" data-oid="ke8hxhp" />
                      </Button>
                      <Button size="icon" variant="ghost" data-oid="df6_x8:">
                        <DownloadIcon className="w-5 h-5" data-oid="_mghun1" />
                      </Button>
                    </div>
                  </div>

                  {/* Audience Table */}
                  <div className="space-y-2" data-oid="_wt8dss">
                    <div
                      className="grid grid-cols-4 text-sm text-muted-foreground py-2"
                      data-oid="31pg_xp"
                    >
                      <div data-oid="md520s8">Channel</div>
                      <div data-oid="42g149d">Users</div>
                      <div data-oid=".bp1hbi">Sessions</div>
                      <div data-oid="ybdtild">Conv. Rate</div>
                    </div>
                    {[
                      {
                        channel: "Social",
                        users: "32K",
                        sessions: "45K",
                        rate: "3.2%",
                      },
                      {
                        channel: "Email",
                        users: "28K",
                        sessions: "36K",
                        rate: "4.5%",
                      },
                      {
                        channel: "Direct",
                        users: "15K",
                        sessions: "22K",
                        rate: "5.1%",
                      },
                    ].map((metric) => (
                      <div
                        key={metric.channel}
                        className="grid grid-cols-4 text-sm py-2 border-t border-border/50"
                        data-oid="ly5y4cz"
                      >
                        <div data-oid="vk8g.9.">{metric.channel}</div>
                        <div data-oid="ibd1jfr">{metric.users}</div>
                        <div data-oid="hbw36fz">{metric.sessions}</div>
                        <div className="font-semibold" data-oid="zm26bo_">
                          {metric.rate}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </MagicCard>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default Analysis;
